"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/general-utils";
import React from "react";

const inputVariants = cva(
  "relative block w-full appearance-none text-foreground disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: cn(
          "rounded-lg bg-white",
          "border border-input hover:border-ring transition-colors",
          "py-1.5 px-3 text-sm placeholder:text-muted-foreground",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        ),
        title: cn(
          "bg-transparent text-lg font-semibold placeholder:text-muted-foreground/60",
          "outline-none border-0 border-b border-transparent transition-colors",
          "py-0 px-0 pr-6",
          "focus:border-primary/40 focus-visible:ring-0 focus-visible:ring-offset-0",
        ),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type InputVariant = NonNullable<
  VariantProps<typeof inputVariants>["variant"]
>;

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "value"
  | "defaultValue"
  | "onChange"
  | "onInput"
  | "onBlur"
  | "onFocus"
  | "onKeyDown"
> {
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Marks the input as invalid (applies red border). */
  invalid?: boolean;
  variant?: InputVariant;
}

const dateTypes = ["date", "datetime-local", "month", "time", "week"];

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onInput,
      onBlur,
      onFocus,
      onKeyDown,
      invalid,
      variant = "default",
      className,
      disabled,
      type = "text",
      ...props
    },
    ref,
  ) => {
    const safeValue = value !== undefined ? String(value) : undefined;
    const safeDefaultValue =
      defaultValue !== undefined ? String(defaultValue) : undefined;

    return (
      <span data-slot="control" className="relative block w-full">
        <input
          ref={ref}
          value={safeValue}
          defaultValue={safeDefaultValue}
          onChange={onChange}
          onInput={onInput}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          disabled={disabled}
          type={type}
          className={cn(
            inputVariants({ variant }),
            invalid &&
              (variant === "title"
                ? "border-destructive focus:border-destructive"
                : "border-destructive hover:border-destructive"),
            type &&
              dateTypes.includes(type) && [
                "[&::-webkit-datetime-edit-fields-wrapper]:p-0",
                "[&::-webkit-date-and-time-value]:min-h-[1.5em]",
                "[&::-webkit-datetime-edit]:inline-flex",
                "[&::-webkit-datetime-edit]:p-0",
                "[&::-webkit-datetime-edit-year-field]:p-0",
                "[&::-webkit-datetime-edit-month-field]:p-0",
                "[&::-webkit-datetime-edit-day-field]:p-0",
                "[&::-webkit-datetime-edit-hour-field]:p-0",
                "[&::-webkit-datetime-edit-minute-field]:p-0",
                "[&::-webkit-datetime-edit-second-field]:p-0",
                "[&::-webkit-datetime-edit-millisecond-field]:p-0",
                "[&::-webkit-datetime-edit-meridiem-field]:p-0",
              ],
            className,
          )}
          {...props}
        />
      </span>
    );
  },
);

Input.displayName = "Input";

export function InputGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="control"
      className={cn(
        "relative isolate block",
        "has-[[data-slot=icon]:first-child]:[&_input]:pl-10 has-[[data-slot=icon]:last-child]:[&_input]:pr-10 sm:has-[[data-slot=icon]:first-child]:[&_input]:pl-8 sm:has-[[data-slot=icon]:last-child]:[&_input]:pr-8",
        "*:data-[slot=icon]:pointer-events-none *:data-[slot=icon]:absolute *:data-[slot=icon]:top-3 *:data-[slot=icon]:z-10 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:top-2.5 sm:*:data-[slot=icon]:size-4",
        "[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5",
        "*:data-[slot=icon]:text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}
